import { toKpiNumberFormat, toNumberScale } from '../../dvm-layout-builder/dvm-builder-number-format.utils';

/**
 * Pure helpers that turn a compute response + tile format into the
 * `app-number-stats` widget data of a KPI tile. Used by the KPI step at
 * Generate time and by the shell when a recompute result lands on a tile.
 */

/** Icon / sub-text / delta decoration keys read from the tile's Format popup. */
export function kpiDecorationData(format?: Record<string, unknown>): Record<string, unknown> {
  return {
    icon: (format?.['icon'] as string) || '',
    iconColor: (format?.['iconColor'] as string) || '',
    iconSize: format?.['iconSize'] ?? '',
    subText: (format?.['subText'] as string) || '',
    deltaText: (format?.['deltaText'] as string) || '',
    deltaDirection: (format?.['deltaDirection'] as string) || 'up',
    deltaColor: (format?.['deltaColor'] as string) || '',
  };
}

/**
 * The single headline value of a compute response: `responseList[0].data`,
 * `data` or the response itself; a row's `value` / `result` / the KPI's own
 * column (`preferredKey`) / first numeric field.
 */
export function extractKpiValue(response: Record<string, unknown> | null, preferredKey = ''): number | string | null {
  if (response == null) return null;
  const list = (response as { responseList?: Array<Record<string, unknown>> }).responseList;
  const fromList = Array.isArray(list) ? (list[0]?.['data'] as unknown) : undefined;
  const dataField = fromList ?? (response as { data?: unknown }).data ?? response;
  const row = Array.isArray(dataField) ? dataField[0] : dataField;
  if (row == null) return null;
  if (typeof row === 'number' || typeof row === 'string') return row;
  const record = row as Record<string, unknown>;
  const candidate =
    record['value'] ?? record['result'] ?? (preferredKey ? record[preferredKey] : undefined) ??
    Object.values(record).find((v) => typeof v === 'number');
  return (candidate as number | string | undefined) ?? null;
}

export interface KpiWidgetDataContext {
  tileId: string;
  title: string;
  format?: Record<string, unknown>;
  /** The KPI's aggregated column — a fallback key when the response row has no `value`/`result`. */
  preferredKey?: string;
}

/** Widget data for `app-number-stats-shared` from a compute response. */
export function buildKpiWidgetData(response: Record<string, unknown> | null, ctx: KpiWidgetDataContext): Record<string, unknown> {
  const { format } = ctx;
  const value = extractKpiValue(response, ctx.preferredKey);
  return {
    title: ctx.title.trim() || 'KPI',
    key: 'value',
    widgetId: 'dvm-tile-kpi-' + ctx.tileId,
    alignment: (format?.['contentAlignment'] as string) || 'center',
    cardLayout: (format?.['cardLayout'] as string) || '',
    titleFontSize: format?.['titleFontSize'] ?? '',
    valueFontSize: format?.['valueFontSize'] ?? '',
    ...kpiDecorationData(format),
    numberFormat: toKpiNumberFormat(format),
    numberScale: toNumberScale(format?.['numberFormat']),
    decimals: Number(format?.['decimals'] ?? 1),
    prefix: (format?.['prefix'] as string) || '',
    suffix: (format?.['suffix'] as string) || '',
    infoText: (format?.['infoText'] as string) || '',
    infoIconPosition: (format?.['infoIconPosition'] as string) || 'corner',
    data: { data: [{ value }] },
  };
}
